<template>
  <div class="user-management-container">
    <AppHeader title="User Management" back-to="/profile" back-text="Back to Profile"/>
    
    <main class="main-content">
      <div class="users-table-container">
        <h2>All Users</h2>
        <table class="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.email">
              <td>{{ user.email }}</td>
              <td>{{ user.name }}</td>
              <td>
                <span :class="['role-badge', user.role]">{{ user.role }}</span>
              </td>
              <td>
                <select :value="user.role" @change="updateRole(user.email, $event.target.value)">
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </main>

    <CustomPopup
      :show="popup.show"
      :type="popup.type"
      :title="popup.title"
      :message="popup.message"
      @close="popup.show = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { COLORS, FONTS } from '@/styles/brand'
import AppHeader from './AppHeader.vue'
import CustomPopup from './CustomPopup.vue'

const users = ref([])
const popup = ref({ show: false, type: '', title: '', message: '' })

const loadUsers = async () => {
  const querySnapshot = await getDocs(collection(db, 'users'))
  users.value = querySnapshot.docs.map(doc => ({
    email: doc.id,
    ...doc.data()
  }))
}

const updateRole = async (email, newRole) => {
  try {
    await updateDoc(doc(db, 'users', email), { role: newRole })
    const user = users.value.find(u => u.email === email)
    if (user) user.role = newRole
    popup.value = { show: true, type: 'success', title: 'Success', message: 'User role updated' }
  } catch (error) {
    popup.value = { show: true, type: 'error', title: 'Error', message: 'Failed to update role' }
  }
}

onMounted(loadUsers)
</script>

<style scoped>
.user-management-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(20, 64, 154, 0.8) 50%, rgba(31, 31, 79, 0.9) 100%),
    url('@/assets/nordeus-3840x1080-pattern_new.jpg') no-repeat center center fixed;
  background-size: cover;
  font-family: v-bind('FONTS.GOOGLE');
}

.main-content {
  flex: 1;
  padding: 40px;
}

.users-table-container {
  max-width: 1200px;
  margin: 0 auto;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}

h2 {
  color: v-bind('COLORS.PRIMARY.WHITE');
  margin: 0 0 24px 0;
  font-size: 1.5rem;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.users-table th,
.users-table td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.users-table th {
  color: v-bind('COLORS.PRIMARY.WHITE');
  font-weight: 600;
  background: rgba(255, 255, 255, 0.1);
}

.users-table td {
  color: rgba(255, 255, 255, 0.9);
}

.role-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
}

.role-badge.admin {
  background: v-bind('COLORS.PRIMARY.RED');
  color: white;
}

.role-badge.user {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.users-table select {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: v-bind('COLORS.PRIMARY.WHITE');
  cursor: pointer;
  font-family: inherit;
}
</style>
